import { FsnHeader } from "@fusion/components/Header";
import { FsnPopoverHeaderAction } from "@fusion/components/PopoverHeaderAction";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import EmailIcon from "@mui/icons-material/Email";
import SettingsIcon from "@mui/icons-material/Settings";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import PersonIcon from "@mui/icons-material/Person";
import {
	Avatar,
	Divider,
	Drawer,
	IconButton,
	Typography,
	Box,
	alpha,
	Tooltip,
} from "@mui/material";
import { DrawerNavigation } from "components";
import * as hooks from "lib/hooks";
import { UseUserRoles } from "lib/hooks/useUserRoles";
import { Outlet, RouterProvider, useNavigate, Link } from "react-router-dom";
import { FormProvider } from "lib/hooks";
import * as util from "lib/util";
import { useState, useMemo, useEffect } from "react";
import * as routes from "enums/routes";
import * as appPath from "enums/appPath";
import { useStore, useSelector } from "react-redux";
import { Info } from "@mui/icons-material";
import { AppSettings } from "lib/api/appSettings";
import { BuildInfo } from "components";
import * as constants from "lib/api";
import { MccApi } from "lib/api";
import { Store } from "redux";
import packageJson from "../package.json";
import { AvatarStyle } from "lib/styles/avatar";
import { HeaderBoxStyle, HeaderLogoSwap } from "lib/styles/header";
import { DrawerPaperStyle } from "lib/styles/drawer";
import { AccountInfo } from "@azure/msal-browser";
import * as utils from "lib/util/constants";
import { UseSectorsAndPermissions } from "lib/hooks/useSectorsAndPermissions";
import { TokenSlice } from "store";
import { isAxiosError } from "axios";

/** Actuator-compatible JSON from GET .../internal/build-info */
type serviceBuildInfoResponse = {
	build?: { artifact?: string };
};

/** JSON from GET .../internal/environment */
type environmentResponse = {
	environment?: string;
};

function logBuildInfoFetchError(path: string, error: unknown): void {
	if (isAxiosError(error)) {
		const status = error.response?.status;
		console.error(
			`Error fetching build info (${path}):`,
			error.message,
			status != null ? `HTTP ${status}` : ""
		);
		return;
	}
	console.error(
		`Error fetching build info (${path}):`,
		error instanceof Error ? error.message : String(error)
	);
}

function logEnvironmentFetchError(path: string, error: unknown): void {
	if (isAxiosError(error)) {
		const status = error.response?.status;
		console.error(
			`Error fetching environment (${path}):`,
			error.message,
			status != null ? `HTTP ${status}` : ""
		);
		return;
	}
	console.error(
		`Error fetching environment (${path}):`,
		error instanceof Error ? error.message : String(error)
	);
}

function logCombineConfigsError(error: unknown): void {
	if (isAxiosError(error)) {
		const status = error.response?.status;
		console.error(
			"Error combining configs:",
			error.message,
			status != null ? `HTTP ${status}` : ""
		);
		return;
	}
	console.error(
		"Error combining configs:",
		error instanceof Error ? error.message : String(error)
	);
}

// Material You animation timing
const emphasized = "cubic-bezier(0.2, 0, 0, 1.0)";
const duration = 250;

const getClaim = (account: AccountInfo, claimName: string): unknown | null => {
	if (account.idTokenClaims && claimName in account.idTokenClaims) {
		return account.idTokenClaims[claimName];
	}
	return null;
};

export function MenuComponent({
	children,
	icon,
}: {
	children: React.ReactNode;
	icon: React.ReactNode;
}) {
	return (
		<FsnPopoverHeaderAction icon={icon}>{children}</FsnPopoverHeaderAction>
	);
}

export function Root() {
	const [drawerOpen, setDrawerOpen] = useState(false);
	const navigate = useNavigate();
	const { account } = hooks.UseAuth();
	const { sectorAdminRoles, plantUserRoles, allRoles: userRoles } = UseUserRoles();
	const toggleDrawer =
		(fn?: (...args: any[]) => void) =>
		(...args: any[]) => {
			setDrawerOpen((prev) => !prev);
			fn?.(...args);
		};
	const theme = hooks.UseTheme();
	const indexes = {
		directoryStart: 0,
		directoryEnd: 3,
		subDirectoryStart: 3,
		subDirectoryEnd: 6,
	};

	let profileImageUrl: string | null = null;
	account.IfSome((account) => {
		const gmin = getClaim(account, "gmuniqueidentifier") as string;
		const directory = gmin
			? gmin.slice(indexes.directoryStart, indexes.directoryEnd)
			: null;
		const subDirectory = gmin
			? gmin.slice(indexes.subDirectoryStart, indexes.subDirectoryEnd)
			: null;
		profileImageUrl = `${utils.UserProfileImageUrl}/${directory}/${subDirectory}/${gmin}.jpg`;
	});
	const { theme: muiTheme } = hooks.UseTheme();
	const c = hooks.UseMaterialColors();
	
	const { sectors } = UseSectorsAndPermissions();
	
	// Get unique sectors from user roles
	const userSectors = useMemo(() => {
		const sectorMap = new Map<string, { id: string; name: string }>();
		userRoles.forEach((role) => {
			if (role.containmentSectorId && role.containmentSectorName) {
				sectorMap.set(role.containmentSectorId, {
					id: role.containmentSectorId,
					name: role.containmentSectorName,
				});
			}
		});
		return Array.from(sectorMap.values());
	}, [userRoles]);

	const isDarkMode = theme.mode === "dark";

	const store = useStore() as Store;
	const token = useSelector(TokenSlice.selectors.selectToken);

	useEffect(() => {
		const api = MccApi.GetInstance(store);
		const paths = constants.Paths();

		const fetchBuildInfo = (path: string) =>
			token.IsEmpty()
				? Promise.resolve(null)
				: api
						.Get<serviceBuildInfoResponse>(path)
						.catch((error) => {
							console.error(
								`Error fetching build info (${path}):`,
								error
							);
							return null;
						});

		Promise.all([
			fetchBuildInfo(paths.mccInternalBuildInfo),
			fetchBuildInfo(paths.traceInternalBuildInfo),
			fetchBuildInfo(paths.securityInternalBuildInfo),
		])
			.then((responses) => {
				const combinedConfig: Record<string, any> = {};

				for (const response of responses) {
					if (response?.build?.artifact) {
						combinedConfig[response.build.artifact] = response;
					}
				}

				AppSettings.setAppConfig(combinedConfig);
			})
			.catch((error: unknown) => {
				logCombineConfigsError(error);
			});
	}, [store, token]);

	// Environment name comes authoritatively from the containment service
	// (GET /internal/environment) instead of being inferred by string-matching
	// the API gateway URL.
	const [environmentDisplay, setEnvironmentDisplay] = useState("Unknown");

	useEffect(() => {
		// Clear first so a token change (including logout) never leaves the
		// previous environment on screen while the new request is in flight.
		setEnvironmentDisplay("Unknown");
		if (token.IsEmpty()) {
			return;
		}
		const api = MccApi.GetInstance(store);
		const paths = constants.Paths();
		// Guard against a slower request from an earlier token resolving after
		// the token has changed and clobbering the newer value.
		let active = true;
		api
			.Get<environmentResponse>(paths.mccInternalEnvironment)
			.then((response) => {
				if (!active) {
					return;
				}
				setEnvironmentDisplay(response?.environment ?? "Unknown");
			})
			.catch((error: unknown) => {
				if (!active) {
					return;
				}
				logEnvironmentFetchError(paths.mccInternalEnvironment, error);
			});
		return () => {
			active = false;
		};
	}, [store, token]);

	const services = [
		constants.MCC_CONTAINMENT_SERVICE,
		constants.MCC_TRACE_SERVICE,
		constants.MCC_SECURITY_SERVICE,
	];

	// eslint-disable-next-line @typescript-eslint/naming-convention
	const InfoButtonBody = () => {
		const appConfig = AppSettings.getAppConfig() ?? {};

		return (
			<Box>
				<Typography align="center" variant="subtitle2" gutterBottom>
					MCC Environment Details
				</Typography>
				<hr />
				<Typography variant="body2">
					<b>MCC Version: </b> v{packageJson.version}
					<br />
					<b>Environment: </b>
					{environmentDisplay}
					<br />
					<b>UI Build: </b>
					{packageJson.version}
					<br />
					{services.Map((service) => (
						<BuildInfo
							appConfig={appConfig[service]}
							title={`${service.Replace("mcc-", "").Replace("-", " ").Title()} Build`}
						/>
					))}
				</Typography>
			</Box>
		);
	};

	return (
		<>
			<Box
				data-test-selector="header-title"
				sx={[HeaderBoxStyle, HeaderLogoSwap]}
			>
				<FsnHeader
					title="Manufacturing Central Containment"
					menuOnClick={toggleDrawer()}
					menuVisible={true}
				>
					<Tooltip
						title={
							isDarkMode
								? "Switch to light mode"
								: "Switch to dark mode"
						}
					>
						<IconButton
							onClick={theme.toggleTheme}
							sx={{
								m: 0.5,
								width: 45,
								height: 45,
								borderRadius: "50%",
							}}
						>
							{isDarkMode ? <LightModeIcon /> : <DarkModeIcon />}
						</IconButton>
					</Tooltip>

					<MenuComponent
						icon={
							<Info
								sx={{
									m: 0.5,
									width: 30,
									height: 30,
									color: c.secondary,
								}}
							/>
						}
					>
						<Box
							sx={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
							}}
						>
							<InfoButtonBody />
						</Box>
					</MenuComponent>

					{account
						.Map((account) => (
							<MenuComponent
								key={account.name}
								icon={
									<Avatar
										src={profileImageUrl || ""}
										alt={util.GetInitials(
											account.name || "??"
										)}
										sx={AvatarStyle(muiTheme, c.primary)}
									>
										{!profileImageUrl &&
											util.GetInitials(
												account.name || "??"
											)}
									</Avatar>
								}
							>
								<Box
									sx={{
										display: "flex",
										flexDirection: "column",
										minWidth: "280px",
									}}
								>
									{/* User Info Section */}
									<Box
										sx={{
											p: 2,
											backgroundColor: alpha(
												c.primary,
												0.08
											),
											borderRadius: 1,
											mb: 1,
											display: "flex",
											flexDirection: "column",
											alignItems: "center",
											textAlign: "center",
										}}
									>
										<Box
											sx={{
												display: "flex",
												flexDirection: "column",
												alignItems: "center",
												gap: 0.5,
											}}
										>
											<Typography
												variant="subtitle1"
												fontWeight="bold"
											>
												{account.name}
											</Typography>
										</Box>
										{!isNil(
											account.idTokenClaims?.mail
										) && (
											<Typography
												variant="body2"
												color="text.secondary"
												sx={{ mt: 1 }}
											>
												{String(
													account.idTokenClaims.mail
												)}
											</Typography>
										)}
									</Box>

									{/* Permissions Section */}
									{(sectorAdminRoles.length > 0 || plantUserRoles.length > 0) && (
										<Box
											sx={{
												px: 2,
												pt: 1.5,
												pb: 0.5,
												width: "100%",
												display: "flex",
												flexDirection: "column",
												alignItems: "flex-start",
												gap: 0.5,
											}}
										>
											{/* Show Sector Admin roles */}
											{sectorAdminRoles.map((role, index) => (
												<Typography
													key={`admin-${role?.containmentSectorName || index}-${index}`}
													sx={{
														fontSize: "0.875rem",
														fontWeight: 500,
														color: c.onSurface,
														px: 1.5,
														py: 0.75,
													}}
												>
													{role?.containmentSectorName || "Unknown"} - Sector Admin
												</Typography>
											))}
											{/* Show Plant User roles */}
											{plantUserRoles.map((role, index) => (
												<Typography
													key={`plant-${role?.containmentSectorName || index}-${index}`}
													sx={{
														fontSize: "0.875rem",
														fontWeight: 500,
														color: c.onSurface,
														px: 1.5,
														py: 0.75,
													}}
												>
													{role?.containmentSectorName || "Unknown"} - Plant User
												</Typography>
											))}
										</Box>
									)}

									<Divider sx={{ mt: 0.5, mb: 1 }} />

									{/* Menu Items Section */}
									<Box
										sx={{
											display: "flex",
											flexDirection: "column",
											gap: 0.25,
										}}
									>
										{(sectorAdminRoles.length > 0 || plantUserRoles.length > 0) && (
											<Link
												to={appPath.Permissions().Path()}
												style={{
													textDecoration: "none",
													color: "inherit",
												}}
												onClick={() => setDrawerOpen(false)}
											>
												<Box
													sx={{
														"display": "flex",
														"alignItems": "center",
														"gap": 2,
														"px": 2,
														"py": 1.5,
														"borderRadius": 1,
														"cursor": "pointer",
														"&:hover": {
															backgroundColor: alpha(
																c.primary,
																0.08
															),
														},
													}}
												>
													<SettingsIcon
														sx={{
															fontSize: 20,
															color: "text.secondary",
														}}
													/>
													<Typography variant="body2">
														View Permissions
													</Typography>
												</Box>
											</Link>
										)}
										<Link
											to={appPath
												.EmailNotificationSettings()
												.Path()}
											style={{
												textDecoration: "none",
												color: "inherit",
											}}
											onClick={() => setDrawerOpen(false)}
										>
											<Box
												sx={{
													"display": "flex",
													"alignItems": "center",
													"gap": 2,
													"px": 2,
													"py": 1.5,
													"borderRadius": 1,
													"cursor": "pointer",
													"&:hover": {
														backgroundColor: alpha(
															c.primary,
															0.08
														),
													},
												}}
											>
												<EmailIcon
													sx={{
														fontSize: 20,
														color: "text.secondary",
													}}
												/>
												<Typography variant="body2">
													Email Notification Settings
												</Typography>
											</Box>
										</Link>
									</Box>

									{/* MCC Access Contact InformationLink */}
									{userSectors.length > 0 && (
										<>
											<Link
												to={routes.MCCAccessContactInfoRoute.Path}
												style={{
													textDecoration: "none",
													color: "inherit",
												}}
												onClick={() => setDrawerOpen(false)}
											>
												<Box
													sx={{
														"display": "flex",
														"alignItems": "center",
														"gap": 2,
														"px": 2,
														"py": 1.5,
														"borderRadius": 1,
														"cursor": "pointer",
														"&:hover": {
															backgroundColor: alpha(
																c.primary,
																0.08
															),
														},
													}}
												>
													<SupervisorAccountIcon
														sx={{
															fontSize: 20,
															color: "text.secondary",
														}}
													/>
													<Typography variant="body2">
														MCC Access Contact Information
													</Typography>
												</Box>
											</Link>
										</>
									)}
								</Box>
							</MenuComponent>
						))
						.UnwrapOrNil()}
				</FsnHeader>
			</Box>

			<Drawer
				open={drawerOpen}
				onClose={toggleDrawer()}
				PaperProps={DrawerPaperStyle}
				SlideProps={{
					style: {
						transition: `transform ${duration}ms ${emphasized}`,
					},
				}}
				variant="temporary"
				ModalProps={{
					keepMounted: true,
				}}
			>
				<Box
					sx={{
						height: "100%",
						display: "flex",
						flexDirection: "column",
						overflow: "hidden",
					}}
				>
					<DrawerNavigation onClick={toggleDrawer(navigate)} />
				</Box>
			</Drawer>

			<Outlet />
		</>
	);
}

export function AppWithAuth() {
	const routeManager = hooks.UseRouteManager();
	return (
		<hooks.SnackBarProvider>
			<hooks.ModalProvider>
				<FormProvider>
					<hooks.AuthProvider>
						<RouterProvider router={routeManager.CreateRouter()} />
					</hooks.AuthProvider>
				</FormProvider>
			</hooks.ModalProvider>
		</hooks.SnackBarProvider>
	);
}
